/**
 * COPY STRATEGY — VatChanges2025Page
 *
 * Awareness:      Problem-Aware (Stage 2/5)
 *                 Urgent regulatory change. Reader may or may not know about
 *                 the new PVM rules but needs to act before the deadline.
 *
 * Sophistication: Stage 1 — Direct Claim
 *                 "SVARBU" + "PRIVALO žinoti" — direct, no-frills urgency.
 *                 Time-sensitive compliance content. No mechanism needed,
 *                 just "here's what changed and what you must do."
 *
 * Avatar:         All active Bolt/Uber/Wolt drivers.
 *                 Time-sensitive — targets everyone before the May 1 deadline.
 *                 Works across all experience levels.
 *
 * Hook:           Urgency/authority — "SVARBU: PVM pakeitimai nuo gegužės 1 d."
 * Structure:      Alert → What changed → Who's affected → What to do → CTA
 * Proof:          Specific regulation changes, effective dates, compliance steps
 * Funnel:         Timely content driving immediate action, leads to CommissionVatPage
 */
import { motion } from 'framer-motion'
import { PiCalendarCheck, PiWarning, PiCheckCircle, PiArrowRight } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const VatChanges2025Page = () => {
  usePageSetup('SVARBU: PVM pakeitimai nuo gegužės 1 d. – ką pavežėjai PRIVALO žinoti', {
    scroll: true,
    description: 'Nuo 2025 m. gegužės 1 d. visi Bolt, Uber ir Wolt vairuotojai PRIVALO registruotis PVM mokėtojais. SVS arba pilnas PVM — sužinokite, kurią schemą pasirinkti ir ką daryti.',
    type: 'article',
    datePublished: '2026-01-15',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText
          as="h1"
          className="text-zinc-900 text-2xl font-medium mt-3 mb-2"
        >
          PVM pakeitimai nuo 2025 gegužės 1 dienos pavežėjams
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">PVM</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">5 min skaitymo</span>
        </MotionDiv>

        <MotionDiv className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl mb-4">
          <div className="flex gap-3">
            <PiCalendarCheck className="min-w-6 h-6 text-red-700" />
            <div>
              <MotionText as="h3" className="text-red-900 text-base font-medium mb-2">
                Svarbu! Nuo 2025 m. gegužės 1 d.
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm mb-2">
                Įsigalioja nauji PVM reikalavimai įsigyjant paslaugas iš užsienio (Bolt, Uber, Wolt). Šie pakeitimai tiesiogiai veikia pavežėjų ir kurjerių atvirkštinio PVM deklaravimą.
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm">
                <strong>Visi pavežėjai PRIVALO registruotis PVM mokėtojais</strong> — nėra pajamų ribos! Registruotis reikia per <strong>3 darbo dienas</strong>.
              </MotionText>
              <MotionText as="p" className="text-red-700 text-xs mt-2">
                45 000 € riba lemia tik schemą: SVS (PVM101) iki 45k, pilnas PVM (FR0600) virš 45k.
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-6"
        >
          Nuo 2025 m. gegužės 1 d. keičiasi PVM deklaravimo tvarka asmenims, įsigijantiems paslaugas iš užsienio ES valstybių. Kas konkrečiai keisis pavežėjams?
        </MotionText>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          PVM mokėtojo riba: kada privalote registruotis?
        </MotionText>

        <MotionDiv className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-5 h-5 text-red-700 mt-0.5" />
            <div>
              <MotionText as="h3" className="text-red-900 text-sm font-medium mb-2">
                Privaloma registracija — nėra pajamų ribos!
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm mb-2">
                <strong>Visi asmenys, įsigiję paslaugų iš užsienio apmokestinamųjų asmenų, privalo registruotis PVM mokėtojais.</strong> Tai taikoma nepriklausomai nuo pajamų dydžio.
              </MotionText>
              <MotionText as="p" className="text-red-700 text-xs mb-3">
                Pagal VMI taisykles, paslaugos iš Bolt, Uber, Wolt, Google, Facebook ir kitų ES apmokestinamųjų asmenų reikalauja PVM registracijos per <strong>3 darbo dienas</strong> nuo paslaugos įsigijimo.
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm font-medium mb-2">
                Ką reiškia 45 000 € riba?
              </MotionText>
              <MotionText as="p" className="text-red-700 text-xs mb-2">
                <strong>45 000 € riba lemia tik tai, kokią schemą galite taikyti</strong> — ne tai, ar reikia registruotis. Tai metinių pajamų iš ekonominės veiklos Lietuvoje riba.
              </MotionText>
              <ul className="space-y-2 text-red-700 text-xs">
                <li className="flex gap-2">
                  <span className="text-red-600">▸</span>
                  <span><strong>SVS (Smulkiojo verslo schema)</strong> — pajamos iki 45 000 €/metus. Deklaruojate PVM101, nereikia išrašinėti PVM sąskaitų klientams, nereikia teikti i.SAF registrų.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-red-600">▸</span>
                  <span><strong>Pilnas PVM mokėtojas</strong> — pajamos virš 45 000 €/metus. Deklaruojate FR0600, privalote išrašinėti PVM sąskaitas, teikti i.SAF registrus iki 20 d.</span>
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          PVM pakeitimai nuo 2025 m. gegužės 1 d.: kas konkrečiai keičiasi?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-sm mb-4">
          Nuo 2025 m. gegužės 1 d. įsigaliojo esminiai PVM įstatymo pakeitimai, tiesiogiai veikiantys pavežėjus ir kurjerius, dirbančius per Bolt, Uber, Wolt platformas.
        </MotionText>

        <MotionDiv className="space-y-4 mb-6">
          <MotionDiv className="p-4 border border-zinc-250 rounded-xl">
            <div className="flex gap-4 mb-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-zinc-50 rounded-full flex items-center justify-center text-sm font-medium">
                1
              </div>
              <MotionText as="h3" className="text-zinc-900 text-base font-medium">
                Nauja prievolė registruotis PVM mokėtoju
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm mb-2">
              Anksčiau daugelis pavežėjų manė, kad registruotis PVM mokėtoju reikia tik viršijus 45 000 € ribą. <strong>Dabar tai neteisinga.</strong>
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Pagal naujas taisykles, <strong>visi asmenys, įsigiję paslaugų iš užsienio apmokestinamųjų asmenų</strong> (Bolt, Uber, Wolt, Google, Facebook), privalo registruotis PVM mokėtojais — nepriklausomai nuo pajamų.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border border-zinc-250 rounded-xl">
            <div className="flex gap-4 mb-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-zinc-50 rounded-full flex items-center justify-center text-sm font-medium">
                2
              </div>
              <MotionText as="h3" className="text-zinc-900 text-base font-medium">
                SVS — paprastesnė schema mažoms pajamoms
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm mb-2">
              Jei jūsų metinės pajamos iš ekonominės veiklos Lietuvoje neviršija 45 000 €, galite pasirinkti <strong>SVS (Smulkiojo verslo schemą)</strong>.
            </MotionText>
            <ul className="space-y-1 text-zinc-600 text-xs">
              <li>✅ Deklaruojate tik PVM101 formą (ne FR0600)</li>
              <li>✅ Nereikia išrašinėti PVM sąskaitų klientams</li>
              <li>✅ Nereikia teikti i.SAF registrų</li>
              <li>✅ Deklaracija tik už mėnesius, kai įsigijote paslaugų iš užsienio</li>
            </ul>
          </MotionDiv>

          <MotionDiv className="p-4 border border-zinc-250 rounded-xl">
            <div className="flex gap-4 mb-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-zinc-50 rounded-full flex items-center justify-center text-sm font-medium">
                3
              </div>
              <MotionText as="h3" className="text-zinc-900 text-base font-medium">
                Pilnas PVM mokėtojas — virš 45 000 €
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm mb-2">
              Jei metinės pajamos viršija 45 000 €, privalote būti <strong>pilnu PVM mokėtoju</strong>.
            </MotionText>
            <ul className="space-y-1 text-zinc-600 text-xs">
              <li>📋 Deklaruojate FR0600 formą kas mėnesį</li>
              <li>📋 Privalote išrašinėti PVM sąskaitas klientams</li>
              <li>📋 Teikiate i.SAF registrus iki kito mėnesio 20 d.</li>
              <li>📋 FR0564 forma už paslaugas kitoms ES valstybėms</li>
            </ul>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kaip registruotis PVM mokėtoju?
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-6">
          <div className="p-4">
            <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-3">
              Registracija per Mano VMI portalą:
            </MotionText>
            <ol className="space-y-2 text-zinc-700 text-xs">
              <li><strong>1.</strong> Pasirinkite prašymo tipą: „Įregistravimas PVM mokėtoju ir nacionalinės smulkaus verslo schemos dalyviu (PVM SVS LT)"</li>
              <li><strong>2.</strong> Nurodykite pageidaujamą registracijos datą (ne anksčiau kaip 3 darbo dienos)</li>
              <li><strong>3.</strong> Užpildykite pajamų informaciją (praėjusios ir einamieji metai)</li>
              <li><strong>4.</strong> Nurodykite įsigytų paslaugų iš užsienio vertę (Bolt, Uber, Wolt)</li>
              <li><strong>5.</strong> Pateikite prašymą — sprendimas skelbiamas Mano VMI</li>
            </ol>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Ką tai reiškia pavežėjams?
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-8">
          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiArrowRight className="min-w-5 h-5 text-zinc-700" />
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium">
                Atvirkštinis PVM lieka
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm">
              21% PVM nuo Bolt/Uber/Wolt komisinio mokesčio vis dar privalomas - tai nekeičiasi.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiArrowRight className="min-w-5 h-5 text-zinc-700" />
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium">
                Daugiau dokumentų patikros
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm">
              VMI dažniau tikrins, ar komisinio SF atitinka naujus reikalavimus.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiArrowRight className="min-w-5 h-5 text-zinc-700" />
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium">
                Terminai tie patys
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm">
              PVM vis dar reikia deklaruoti ir mokėti iki 25-os kito mėnesio dienos.
            </MotionText>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kas turi būti komisinio SF?
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-3">
            Nauji privalomi duomenys (nuo 2025.05.01)
          </MotionText>
          <MotionDiv className="space-y-2">
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Tiekėjo PVM kodas (Bolt/Uber/Wolt ES PVM kodas)
              </MotionText>
            </MotionDiv>
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Paslaugos pobūdis (komisinis mokestis už platformos naudojimą)
              </MotionText>
            </MotionDiv>
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Pirkėjo (jūsų) IV numeris ir PVM kodas (jei taikoma)
              </MotionText>
            </MotionDiv>
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Suma be PVM ir PVM suma atskirai
              </MotionText>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kaip pasirengti?
        </MotionText>

        <MotionDiv className="space-y-4 mb-8">
          <MotionDiv className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-xl">
            <MotionText as="h4" className="text-green-900 text-sm font-medium mb-2">
              1. Patikrinkite komisinio SF formą
            </MotionText>
            <MotionText as="p" className="text-green-800 text-sm">
              Įsitikinkite, kad Bolt/Uber/Wolt išrašo sąskaitas su visais naujais duomenimis. Jei ne - susisiekite su platformos palaikymu.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-xl">
            <MotionText as="h4" className="text-green-900 text-sm font-medium mb-2">
              2. Saugokite visas sąskaitas
            </MotionText>
            <MotionText as="p" className="text-green-800 text-sm">
              Nuo gegužės 1 d. dokumentų patikra bus griežtesnė - būtina turėti pilnas SF.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-xl">
            <MotionText as="h4" className="text-green-900 text-sm font-medium mb-2">
              3. Sekite VMI atnaujinimus
            </MotionText>
            <MotionText as="p" className="text-green-800 text-sm">
              Atnaujinta PVM101 deklaracija su naujais laukais jau įsigaliojo.
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Baudos už netinkamus dokumentus
              </MotionText>
              <MotionText as="p" className="text-amber-800 text-sm mb-2">
                Nuo gegužės 1 d. VMI gali atmesti PVM atskaitas, jei komisinio SF neturi visų reikalingų duomenų.
              </MotionText>
              <MotionText as="p" className="text-amber-700 text-xs">
                Tai reiškia, kad turėsite mokėti PVM iš savo kišenės, neįtraukus į išlaidas!
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Dažniausiai užduodami klausimai
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-8">
          <div className="p-4">
            <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
              Ar keisis PVM procentas?
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Ne, PVM lieka 21%. Keičiasi tik deklaravimo forma ir dokumentų reikalavimai.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
              Ar reikia kažką keisti iki gegužės 1 d.?
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Iki gegužės 1 d. galite deklaruoti kaip įprastai. Pakeitimai taikomi tik paslaugoms, gautoms nuo 2025.05.01.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
              Ką daryti, jei Bolt/Uber neišrašo tinkamos SF?
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Kreipkitės į platformos palaikymą ir paprašykite atnaujintos sąskaitos su visais duomenimis. Jei atsisako - kreipkitės į VMI konsultaciją.
            </MotionText>
          </div>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
            📢 Stebėkite atnaujinimus
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-sm">
            Šie pakeitimai jau įsigaliojo nuo 2025 m. gegužės 1 d. Jei turite klausimų dėl naujų reikalavimų, kreipkitės į VMI konsultaciją arba naudokite Finhudas — sistema automatizuoja PVM deklaravimą pagal naujausius reikalavimus.
          </MotionText>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM už Bolt komisinius: gidas' },
          { slug: 'mokesciu-kalendorius-kada-moketi-mokescius', title: 'Mokesčių kalendorius 2025' },
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2025' }
        ]} />

        <motion.div
          className="mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ButtonLink variant="secondary" to="/blog">
            Grįžti į straipsnius
          </ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default VatChanges2025Page
